
import { list } from '@fitogest/reactive-form-builder/utils';
import {
    FilteredValue,
    Form,
    FormField,
    FormSection,
    FormType,
    DropdownData,
    InputType,
    ReactiveFieldLogicContext,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { AnalisisModel } from 'src/app/models/analisis/analisis.model';
import { ClienteModel } from 'src/app/models/cliente.model';
import { AnalisisEnvironmentDefinition } from '../../environment-variables';
import { AnalisisListsDefinition } from './list-variables';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import {
  getDropdownSelectedArray,
  getDropdownSelectedSingle,
  setDropdownSelected,
} from '@fitogest/reactive-form-builder/utils';

interface ReactiveValueContext {
  readonly getValue: (fieldName: string) => unknown;
}

export class AnalisisFormDefinition {
  public formType!: FormType;

  public env: AnalisisEnvironmentDefinition = new AnalisisEnvironmentDefinition();

  public lists: AnalisisListsDefinition = new AnalisisListsDefinition();

  public model: ReactiveFormValue<AnalisisModel> = {} as ReactiveFormValue<AnalisisModel>;

  public tag: ReactiveFormChangedField<AnalisisModel> = '';

  public formFieldsSection!: FormSection<object>;

  public formFieldsTitular!: FormSection<object>;

  public formFieldsResponsable!: FormSection<object>;

  public formFields!: Form<AnalisisModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];

  public tareaComercial: DropdownData<string> = list<string>(['...', null], 'Llamada', 'Email', 'Reunión', 'Demo', 'Presupuesto');

  public estadoPresupuestos: DropdownData<string> = list<string>(['...', null], 'Enviado', 'Aplazado', 'Perdidos', 'Confirmado');

  public clientes: DropdownData<ClienteModel> | DropdownData<ClienteModel>[] = list();

  public shouldCreateNextAnalisis = true;

  public fechaSiguiente: string | undefined = undefined;

  public openDialog = false;

  constructor(
    type: FormType,
    _model: Record<string, string>,
    table: (string[] | string[][])[] | null
  ) {
    this.formType = +type;

    const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

    this.formFields = {
      name: 'analisis',
      header: action + ' Recordatorio de Análisis',
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
      uploadButton: {isEnabled: _model['estado'] === 'Realizada', label: 'Adjuntar Análisis'},
      signed: false,
      dropdownCatalogs: {
        critical: ['id_cliente', 'id_finca', 'id_sector', 'ids_parcelas', 'id_comunidad_regantes'],
        deferred: this.env.showAnalisisParcelas ? ['parcelas'] : [],
      }
    };
  }


  private getSelectedClientId(): string {
    return String(this.env.clienteActual?.id ?? '').trim();
  }


  private getDefaultClientControlValue(): string | string[] | null {
    const selectedClientId = this.getSelectedClientId();

    if (selectedClientId === '') {
      return null;
    }

    return this.env.applicationType === 'cropgest' ? [selectedClientId] : selectedClientId;
  }


  private normalizeIdentifier(value: unknown): string {
    return String(value ?? '').trim();
  }


  private hasIdentifier(value: unknown): boolean {
    return this.normalizeIdentifier(value) !== '';
  }


  private isSameIdentifier(first: unknown, second: unknown): boolean {
    const firstId = this.normalizeIdentifier(first);
    const secondId = this.normalizeIdentifier(second);

    return firstId !== '' && secondId !== '' && firstId === secondId;
  }


  private isSelectedClientOption(option: FilteredValue<ClienteModel>): boolean {
    const selectedClientId = this.getSelectedClientId();

    if (selectedClientId === '') {
      return true;
    }

    return String(option.value?.id ?? '').trim() === selectedClientId;
  }


  public getGeneralFields(): FormField<any>[] {
    const isAva = this.env.appName === 'avafitogest';
    const generalFields: FormField<any>[] = [
      {
        name: 'id_cliente',
        label: this.env.clienteName,
        input: {
          type: this.env.applicationType === 'cropgest' ? InputType.MULTISELECT : InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.clientes,
          defaultValue: this.getDefaultClientControlValue(),
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValue<ClienteModel>) => this.isSelectedClientOption(it),
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: !isAva,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasClients && this.env.isAdmin,
        },
        order: 1
      },
      {
        name: 'id_finca',
        label: this.env.fincaName,
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.fincas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValue<FincasModel>) => {
              if (this.env.hasClients && this.env.isAdmin) {
                if (this.env.applicationType === 'cropgest') {
                  const selectedClients = getDropdownSelectedArray(this.lists.clientes)
                    .filter((cliente: ClienteModel) => this.hasIdentifier(cliente.id));

                  if (selectedClients.length === 0) {
                    return true;
                  }

                  return selectedClients.some((cliente: ClienteModel) =>
                    this.isSameIdentifier(cliente.id, it.value?.id_cliente)
                  );
                } else {
                  const selectedClient = getDropdownSelectedSingle(this.lists.clientes);

                  if (!this.hasIdentifier(selectedClient?.id)) {
                    return true;
                  }

                  return this.isSameIdentifier(it.value?.id_cliente, selectedClient?.id);
                }
              } else {
                return true;
              }
            }
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: !isAva,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 2
      },
      {
        name: 'id_sector',
        dependencies: [
          { source: 'id_finca', target: 'id_sector', kind: 'filter', legacy: true },
        ],
        dependencyBehaviour: {
          keepCurrentValueIfStillAvailable: true,
          keepValueUntilOptionsLoaded: true,
          preservePanelOnReload: true,
          preserveFilterOnReload: true,
        },
        label: this.env.appName === 'pyf' ? 'Paraje' : 'Sector',
        input: {
          type: InputType.MULTISELECT,
          isEditable: true,
          data: this.lists.sectores,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValue<SectorModel>) => {
              const selectedFinca = getDropdownSelectedSingle(this.lists.fincas);

              return this.isSameIdentifier(selectedFinca?.id, it.value?.id_finca);

            },
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: !isAva,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisSectorParcelasForm,
        },
        order: 3
      },
      {
        name: 'ids_parcelas',
        dependencies: [
          { source: 'id_finca', target: 'ids_parcelas', kind: 'filter', legacy: true },
          { source: 'id_sector', target: 'ids_parcelas', kind: 'filter', legacy: true },
        ],
        dependencyBehaviour: {
          keepCurrentValueIfStillAvailable: true,
          keepValueUntilOptionsLoaded: true,
          preservePanelOnReload: true,
          preserveFilterOnReload: true,
        },
        label: this.env.parcelaName,
        input: {
          type: InputType.MULTISELECT,
          isEditable: true,
          data: this.lists.lista_parcelas,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (it: FilteredValue<ParcelasModel>) => {
              if (it.value?.activo?.toString() !== '1') {
                return false;
              }

              const selectedSectores = getDropdownSelectedArray(this.lists.sectores)
                .filter((sector: SectorModel) => this.hasIdentifier(sector.id));

              if (selectedSectores.length > 0) {
                return selectedSectores.some((sector: SectorModel) =>
                  this.isSameIdentifier(sector.id, it.value?.id_sector)
                );
              }

              const selectedFinca = getDropdownSelectedSingle(this.lists.fincas);

              return this.isSameIdentifier(it.value?.id_finca, selectedFinca?.id);
            },
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: !isAva,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisSectorParcelasForm,
        },
        order: 4
      },
      {
        name: 'id_comunidad_regantes',
        label: 'Comunidad de Regantes',
        input: {
          type: InputType.DROPDOWN_SEARCH,
          isEditable: true,
          data: this.lists.comunidadesRegantes,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: isAva,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 5,
        buttons: [
          {
            class: 'btn btn-success border rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center',
            style: `
              width: 26px;
              height: 26px;
              margin-left: 6px;
              font-size: .95rem;
              transform: translateY(-0.5rem);
            `,
            icon: 'fa fa-plus',
            label: '',
            tooltip: 'Añadir nuevo valor',
            tooltipPosition: 'top',
            function: () => this.openDialog = true
          }
        ],
      },
      {
        name: 'fecha',
        label: 'Fecha',
        input: { type: InputType.CALENDAR, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 6
      },
      {
        name: 'semana',
        label: 'Semana',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: environment.appName === 'viverosmuzale' ? false : true ,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.applicationType === 'viverogest',
        },
        order: 7
      },
      {
        name: 'remesa',
        label: 'Remesa',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: environment.appName === 'viverosmuzale' ? false : true ,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.applicationType === 'viverogest',
        },
        order: 8
      },
      {
        name: 'tipo_analisis',
        label: 'Tipo de análisis',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.tiposAnalisis
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 9
      },
      {
        name: 'frecuencia',
        label: 'Frecuencia',
        input: {
          type: InputType.DROPDOWN,
          isEditable: this.env.appName !== 'lafast',
          data: this.lists.frecuencias,
          dropdownConfiguration: {
            swap: (it: FormField<object>, context?: ReactiveFieldLogicContext<object>) => {
              switch (this.env.appName) {
                case 'lafast':
                  it.input.data = this.lists.frecuenciasFast;
                  setDropdownSelected(it.input.data as DropdownData<string>, '1 año');
                  break;

                case 'armentia':
                  it.input.data = this.lists.frecuenciasArmentia;
                  break;

                case 'esparragosgranada':
                  it.input.data = this.lists.frecuenciasEsparragos;
                  break;

                default:
                  it.input.data = this.lists.frecuencias;
                  break;
              }

              const selectedAnalisisType = this.resolveTipoAnalisisValue(context);

              if (selectedAnalisisType?.includes('Suelo')) {
                if (selectedAnalisisType === 'Suelo - pH' || selectedAnalisisType === 'Suelo - Conductividad') {
                  it.input.data = this.lists.frecuenciasEsparragos;
                  this.model.frecuencia = 'Semanal';
                  setDropdownSelected(it.input.data as DropdownData<string>, this.model.frecuencia);
                  this.patchFrecuenciaControl(context, this.model.frecuencia);
                } else {
                  it.input.data = this.lists.frecuenciasSuelo;
                }
              } else if (selectedAnalisisType === 'Foliar') {
                it.input.data = this.lists.frecuenciasFoliar;
              } else if (
                selectedAnalisisType === 'Fruta - Naranja' ||
                selectedAnalisisType === 'Fruta - Mandarina' ||
                selectedAnalisisType === 'Fruta - Clementina' ||
                selectedAnalisisType === 'Fruta - Satsuma'
              ) {
                it.input.data = this.lists.frecuenciasFruta;
              } else if (selectedAnalisisType === 'Fruta - Fresón') {
                it.input.data = this.lists.frecuenciasFreson;
              } else if (selectedAnalisisType === 'Residuos') {
                it.input.data = this.lists.frecuenciasResiduos;
              } else if (selectedAnalisisType === 'Agua - Microbiológico') {
                it.input.data = this.lists.frecuenciasAguaM;
              } else if (selectedAnalisisType === 'Agua - Químico') {
                it.input.data = this.lists.frecuenciasAguaQ;
              } else if (selectedAnalisisType === 'Savia') {
                it.input.data = this.lists.frecuenciasSavia;
              }
            },
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 10
      },
      {
        name: 'muestra',
        label: 'Muestra',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 11
      },
      {
        name: 'origen',
        label: 'Origen',
        input: { type: InputType.TEXT, isEditable: this.env.appName === 'fitogest' },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible:
            this.env.appName === 'almassora' ||
            this.env.appName === 'burriana' ||
            this.env.appName === 'lacooperativa' ||
            this.env.appName === 'onda',
        },
        order: 12
      },
      {
        name: 'objeto_analisis',
        label: 'Objeto del análisis',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.lista_analisis
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 12
      },
      {
        name: 'analisis',
        label: 'Referencia Análisis',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisNumeroAnalisis
        },
        order: 13
      },
      {
        name: 'boletin',
        label: 'Nº boletín',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisBoletin,
        },
        order: 14
      },
      {
        name: 'laboratorio',
        label: 'Laboratorio',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisBoletin || this.env.appName === 'avafitogest',
        },
        order: 15
      },
      {
        name: 'direccion_laboratorio',
        label: 'Dirección Laboratorio',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisBoletin || this.env.appName === 'avafitogest',
        },
        order: 16
      },
      {
        name: 'sustancia_activa',
        label: 'Sustancia activa detectada',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisBoletin || this.env.appName === 'avafitogest',
        },
        order: 17
      },
      {
        name: 'estado',
        label: 'Estado',
        input: {
          type: InputType.DROPDOWN,
          isEditable: true,
          data: this.lists.estados,
          defaultValue: 'Programada'
        },
        updateValue: (it) => {
          if (getDropdownSelectedSingle(it.input.data as DropdownData<string>) === 'Realizada') {
            this.formFields.uploadButton = {isEnabled: true, label: 'Adjuntar Análisis'};
          }
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 18
      },
      {
        name: 'materia_activa',
        label: this.env.appName === 'laplana' ? 'Notas (cultivo)' : 'Notas',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 19
      },
      {
        name: 'volumen_kg',
        label: 'Volumen (kg)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 20
      },
      {
        name: 'agua_m',
        label: 'Agua m³',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisM3Agua,
        },
        order: 21
      },
      {
        name: 'mgrl',
        label: 'Nitratos (mg/l)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showAnalisisMgrl || this.env.showCultivosBajoAguaFields,
        },
        order: 22
      },
    ];

    this.fieldNames = this.fieldNames.concat(generalFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(generalFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return generalFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  public getDatosSueloFields(): FormField<any>[] {
    const datosSueloFields: FormField<any>[] = [
      {
        name: 'ph_suelo',
        label: 'pH (suelo)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1
      },
      {
        name: 'mo_suelo',
        label: '% m.o. sobre m.s (0–30 cm)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 2
      },
      {
        name: 'no3_suelo',
        label: '% NO3⁻ sobre m.s (0–30 cm)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 3
      },
      {
        name: 'p2o5_suelo',
        label: '% P₂O₅ sobre m.s (0–30 cm)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 4
      },
      {
        name: 'k2o_suelo',
        label: '% K₂O sobre m.s (0–30 cm)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 5
      },
      {
        name: 'me_suelo',
        label: 'Me (análisis metales pesados – si aplica lodos tratados)',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 6
      }
    ];

    this.fieldNames = this.fieldNames.concat(datosSueloFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(datosSueloFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return datosSueloFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  public getResultadosAnalisisFields(isSectionVisible: boolean): FormField<any>[] {
    const resultadosAnalisisFields: FormField<any>[] = [
      {
        name: 'pH',
        label: 'pH',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
      {
        name: 'conductividad',
        label: 'Conductividad',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 2
      },
      {
        name: 'sulfatos',
        label: 'Sulfatos',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 3
      },
      {
        name: 'nitratos',
        label: 'Nitratos',
        input: { type: InputType.TEXT, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 4
      },
      {
        name: 'valoracion',
        label: 'Valoración',
        input: { type: InputType.DROPDOWN, isEditable: true },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 5
      },
    ];

    this.fieldNames = this.fieldNames.concat(resultadosAnalisisFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(resultadosAnalisisFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return resultadosAnalisisFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private patchFrecuenciaControl(context: ReactiveFieldLogicContext<object> | undefined, value: unknown): void {
    const frecuenciaControl = context?.model?.get('frecuencia');

    if (!frecuenciaControl || frecuenciaControl.value === value) {
      return;
    }

    frecuenciaControl.patchValue(value, { emitEvent: true });
  }


  public getFechaSiguienteFields(): FormField<any>[] {
    const fechaSiguienteFields: FormField<any>[] = [
      {
        name: 'fecha_siguiente_checked',
        label: this.getFechaSiguienteLabel(this.model.fechaSiguiente),
        input: {
          type: InputType.SWITCH,
          isEditable: true,
          defaultValue: this.shouldCreateNextAnalisis
        },
        dependencies: [
          { source: 'fecha', target: 'fecha_siguiente_checked', kind: 'visibility' },
          { source: 'frecuencia', target: 'fecha_siguiente_checked', kind: 'visibility' },
          { source: 'tipo_analisis', target: 'fecha_siguiente_checked', kind: 'visibility' },
        ],
        visibleWhen: {
          dependsOn: ['fecha', 'frecuencia', 'tipo_analisis'],
          resolve: (context): boolean => this.canShowFechaSiguienteFromContext(context),
        },
        labelWhen: {
          dependsOn: ['fecha', 'frecuencia', 'tipo_analisis'],
          resolve: (context): string => this.getFechaSiguienteLabel(
            this.resolveNextAnalysisDate(
              this.resolveFechaValue(context),
              this.resolveFrecuenciaValue(context),
            ),
          ),
        },
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: false,
        },
        order: 1
      },
    ];

    this.fieldNames = this.fieldNames.concat(fechaSiguienteFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(fechaSiguienteFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return fechaSiguienteFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  public getHiddenFields(): FormField<any>[] {
    const hiddenFields: FormField<any>[] = [
      {
        name: 'id',
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.model.id,
          isEditable: false
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1
      },
      {
        name: 'id_usuario',
        label: '',
        input: {
          isEditable: false,
          defaultValue: StorageManager.getClient()?.id_usuario_asignado ?? this.model.id_usuario ?? StorageManager.getUser().id,
          type: InputType.HIDDEN,
        },
        updateValue: () => StorageManager.getClient()?.id_usuario_asignado ?? this.model.id_usuario ?? StorageManager.getUser().id,
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 2
      },
      {
        name: 'adjunto',
        label: '',
        input: { type: InputType.EMPTY, isEditable: false },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 4
      },
      {
        name: 'fechaSiguiente',
        label: '',
        input: { type: InputType.HIDDEN, isEditable: false },
        computed: {
          dependsOn: ['fecha', 'frecuencia', 'tipo_analisis'],
          resolve: (context) => this.resolveNextAnalysisDate(
            this.resolveFechaValue(context),
            this.resolveFrecuenciaValue(context),
          ),
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 5
      }
    ];

    this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(hiddenFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getFormSections(): FormSection<any>[] {
    const sections: FormSection<any>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getGeneralFields(),
        order: 1
      },
      {
        name: 'Datos del Suelo',
        width: 12,
        isVisible: true,
        fields: this.getDatosSueloFields(),
        order: 2
      },
      {
        name: '',
        width: 12,
        isVisible: this.env.appName === 'viverospereira',
        fields: this.getResultadosAnalisisFields(this.env.appName === 'viverospereira'),
        order: 3
      },
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getFechaSiguienteFields(),
        order: 3
      },
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getHiddenFields(),
        order: 4
      },
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  public setNextDate(
    date: Date | string,
    period: number,
    timelapse: moment.unitOfTime.DurationConstructor
  ) {
    const nextDate = this.addFrequencyToDate(date, period, timelapse);

    this.model.fechaSiguiente = nextDate;
  }

  private resolveFechaValue(context?: ReactiveValueContext): Date | string {
    return (context?.getValue('fecha') as Date | string | undefined) ?? this.model.fecha ?? '';
  }

  private resolveTipoAnalisisValue(context?: ReactiveFieldLogicContext<object>): string {
    const candidates: unknown[] = [
      context?.getValue('tipo_analisis'),
      this.model.tipo_analisis,
      getDropdownSelectedSingle(this.lists.tiposAnalisis),
    ];

    return candidates.map((candidate) => this.normalizeTextValue(candidate)).find((candidate) => candidate !== '') ?? '';
  }


  private resolveFrecuenciaValue(context?: ReactiveValueContext): unknown {
    const candidates: unknown[] = [
      context?.getValue('frecuencia'),
      this.model.frecuencia,
      getDropdownSelectedSingle(this.lists.frecuencias),
      getDropdownSelectedSingle(this.lists.frecuenciasFast),
      getDropdownSelectedSingle(this.lists.frecuenciasArmentia),
      getDropdownSelectedSingle(this.lists.frecuenciasEsparragos),
      getDropdownSelectedSingle(this.lists.frecuenciasSuelo),
      getDropdownSelectedSingle(this.lists.frecuenciasFoliar),
      getDropdownSelectedSingle(this.lists.frecuenciasFruta),
      getDropdownSelectedSingle(this.lists.frecuenciasFreson),
      getDropdownSelectedSingle(this.lists.frecuenciasResiduos),
      getDropdownSelectedSingle(this.lists.frecuenciasAguaM),
      getDropdownSelectedSingle(this.lists.frecuenciasAguaQ),
      getDropdownSelectedSingle(this.lists.frecuenciasSavia),
    ];

    return candidates.find((candidate) => this.normalizeFrequencyValue(candidate) !== '') ?? '';
  }

  private normalizeFrequencyValue(frequency: unknown): string {
    return this.normalizeTextValue(frequency);
  }

  private normalizeTextValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const objectCandidate = record['value'] ?? record['label'] ?? record['nombre'];

      return this.normalizeTextValue(objectCandidate);
    }

    return String(value).replace(/\u00a0/g, ' ').trim();
  }

  private canShowFechaSiguienteFromContext(context: ReactiveFieldLogicContext<AnalisisModel>): boolean {
    return this.hasCreatableNextAnalysisDate(
      this.resolveNextAnalysisDate(
        this.resolveFechaValue(context),
        this.resolveFrecuenciaValue(context),
      ),
    );
  }

  private hasCreatableNextAnalysisDate(nextDate: unknown): boolean {
    const canCreateNextAnalysis = this.formType === FormType.CREATION ||
      this.formType === FormType.DUPLICATION ||
      (this.env.appName === 'avafitogest' && this.formType === FormType.EDITION);

    return canCreateNextAnalysis && String(nextDate ?? '').trim() !== '';
  }

  private getFechaSiguienteLabel(nextDate: unknown): string {
    return 'Crear también el análisis siguiente (programado para el día ' + String(nextDate ?? '') + ')';
  }

  private resolveNextAnalysisDate(date: Date | string, frequency: unknown): string | null {
    const frequencyConfiguration = this.getFrequencyConfiguration(frequency);

    if (frequencyConfiguration === null) {
      return null;
    }

    return this.addFrequencyToDate(date, frequencyConfiguration.period, frequencyConfiguration.timelapse);
  }

  private getFrequencyConfiguration(
    frequency: unknown,
  ): { period: number; timelapse: moment.unitOfTime.DurationConstructor } | null {
    switch (this.normalizeFrequencyValue(frequency)) {
      case 'Semanal':
        return { period: 7, timelapse: 'day' };
      case 'Quincena':
        return { period: 15, timelapse: 'day' };
      case '1 mes':
        return { period: 1, timelapse: 'month' };
      case '2 meses':
        return { period: 2, timelapse: 'month' };
      case '3 meses':
        return { period: 3, timelapse: 'month' };
      case '4 meses':
        return { period: 4, timelapse: 'month' };
      case '6 meses':
        return { period: 6, timelapse: 'month' };
      case '1 año':
        return { period: 1, timelapse: 'year' };
      case '2 años':
        return { period: 2, timelapse: 'year' };
      case '3 años':
        return { period: 3, timelapse: 'year' };
      case '4 años':
        return { period: 4, timelapse: 'year' };
      case '5 años':
        return { period: 5, timelapse: 'year' };
      default:
        return null;
    }
  }

  private addFrequencyToDate(
    date: Date | string,
    period: number,
    timelapse: moment.unitOfTime.DurationConstructor,
  ): string | null {
    if (!date || !period || !timelapse) {
      return null;
    }

    const parsedDate = typeof date === 'string' ? moment(date, 'DD/MM/YYYY', true) : moment(date);

    if (!parsedDate.isValid()) {
      return null;
    }

    return parsedDate.add(period, timelapse).format('DD/MM/YYYY');
  }
}